/**
 * useFilters — Hook genérico para manejar estado de filtros.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { currentMonthRange } from '@/lib/utils'
import type { FilterConfig } from './types'

interface UseFiltersOpts {
  onFilterChange?: () => void
  /**
   * Si se pasa, el estado de filtros (values + búsqueda) se persiste en
   * sessionStorage bajo esta clave y se restaura al montar. Sirve para preservar
   * la vista del listado al entrar a una vista de detalle (p. ej. Vista 360) y
   * volver. Opt-in: sin clave, comportamiento idéntico al anterior.
   */
  persistKey?: string
}

export function useFilters(config: FilterConfig, opts?: UseFiltersOpts) {
  const persistKey = opts?.persistKey
  // Build initial state from config
  const initialState = useMemo(() => {
    const state: Record<string, string> = {}
    for (const f of config) {
      if (f.type === 'date-range') {
        // Pre-cargar el periodo actual cuando el filtro lo pide, para no
        // traer todo el histórico por defecto.
        if (f.defaultPreset === 'current-month') {
          const { from, to } = currentMonthRange()
          state[`${f.key}_from`] = from
          state[`${f.key}_to`] = to
        } else {
          state[`${f.key}_from`] = ''
          state[`${f.key}_to`] = ''
        }
      } else if (f.type === 'number-range') {
        state[`${f.key}_min`] = ''
        state[`${f.key}_max`] = ''
      } else {
        state[f.key] = ''
      }
    }
    return state
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Restaurar desde sessionStorage si hay persistKey (los valores se comparan
  // contra `initialState` (defaults de config) para "untouched"/reset, por eso
  // initialState NO se sobrescribe; solo el valor inicial del estado).
  const persisted = useMemo(() => {
    if (!persistKey) return null
    try {
      const raw = sessionStorage.getItem(`filters:${persistKey}`)
      return raw ? (JSON.parse(raw) as { values?: Record<string, string>; searchRaw?: string }) : null
    } catch { return null }
  }, [persistKey])

  const [values, setValues] = useState<Record<string, string>>(
    () => (persisted?.values ? { ...initialState, ...persisted.values } : initialState),
  )

  // Debounced search
  const searchDef = config.find(f => f.type === 'search')
  const debounceMs = searchDef?.type === 'search' ? (searchDef.debounceMs ?? 400) : 400
  const [searchRaw, setSearchRaw] = useState(persisted?.searchRaw ?? '')
  const [searchDebounced, setSearchDebounced] = useState(persisted?.searchRaw ?? '')

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchRaw)
      if (searchRaw !== searchDebounced) opts?.onFilterChange?.()
    }, debounceMs)
    return () => clearTimeout(timer)
  }, [searchRaw, debounceMs]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persistir el estado en sessionStorage (si hay persistKey).
  useEffect(() => {
    if (!persistKey) return
    try {
      sessionStorage.setItem(`filters:${persistKey}`, JSON.stringify({ values, searchRaw }))
    } catch { /* storage lleno/denegado: ignorar */ }
  }, [persistKey, values, searchRaw])

  // Prevent onFilterChange on initial render
  const isFirst = useRef(true)

  const setFilter = useCallback((key: string, value: string) => {
    // Search uses its own debounce
    const def = config.find(f => f.key === key || f.key === key.replace(/_(from|to|min|max)$/, ''))
    if (def?.type === 'search') {
      setSearchRaw(value)
      return
    }
    setValues(prev => ({ ...prev, [key]: value }))
    if (!isFirst.current) opts?.onFilterChange?.()
  }, [config, opts])

  useEffect(() => { isFirst.current = false }, [])

  // Setea ambos extremos de un date-range a la vez (presets rápidos), con un
  // solo onFilterChange.
  const setRange = useCallback((key: string, from: string, to: string) => {
    setValues(prev => ({ ...prev, [`${key}_from`]: from, [`${key}_to`]: to }))
    if (!isFirst.current) opts?.onFilterChange?.()
  }, [opts])

  const resetAll = useCallback(() => {
    setValues(initialState)
    setSearchRaw('')
    setSearchDebounced('')
    if (persistKey) {
      try { sessionStorage.removeItem(`filters:${persistKey}`) } catch { /* ignore */ }
    }
    opts?.onFilterChange?.()
  }, [initialState, opts, persistKey])

  const resetFilter = useCallback((key: string) => {
    const def = config.find(f => f.key === key)
    if (def?.type === 'search') {
      setSearchRaw('')
      setSearchDebounced('')
    } else if (def?.type === 'date-range') {
      setValues(prev => ({ ...prev, [`${key}_from`]: '', [`${key}_to`]: '' }))
    } else if (def?.type === 'number-range') {
      setValues(prev => ({ ...prev, [`${key}_min`]: '', [`${key}_max`]: '' }))
    } else {
      setValues(prev => ({ ...prev, [key]: '' }))
    }
    opts?.onFilterChange?.()
  }, [config, opts])

  // Build clean params for API (strip empty strings)
  const params = useMemo(() => {
    const p: Record<string, string | undefined> = {}
    for (const [k, v] of Object.entries(values)) {
      if (v) p[k] = v
    }
    // Add debounced search
    if (searchDef && searchDebounced) {
      p[searchDef.key] = searchDebounced
      // Búsqueda explícita "escapa" del rango de fecha por defecto: si el usuario
      // está buscando y un date-range sigue en su preset inicial (no lo tocó),
      // omitimos el rango para buscar en TODO el historial. Si eligió un rango a
      // propósito, se respeta (search + fecha en AND). Así un folio/RUT antiguo
      // aparece sin tener que adivinar y abrir el filtro de fecha.
      for (const f of config) {
        if (f.type === 'date-range' && f.defaultPreset === 'current-month') {
          const fromKey = `${f.key}_from`
          const toKey = `${f.key}_to`
          const untouched =
            values[fromKey] === initialState[fromKey] &&
            values[toKey] === initialState[toKey]
          if (untouched) {
            delete p[fromKey]
            delete p[toKey]
          }
        }
      }
    }
    return p
  }, [values, searchDebounced, searchDef, config, initialState])

  // True cuando una búsqueda activa está ignorando un date-range que sigue en su
  // default (current-month) — para mostrar el aviso "buscando en todo el historial".
  const searchOverridesDateRange = useMemo(() => {
    if (!searchDebounced) return false
    return config.some(
      f =>
        f.type === 'date-range' &&
        f.defaultPreset === 'current-month' &&
        values[`${f.key}_from`] === initialState[`${f.key}_from`] &&
        values[`${f.key}_to`] === initialState[`${f.key}_to`],
    )
  }, [config, values, searchDebounced, initialState])

  const hasActiveFilters = Object.values(values).some(v => v !== '') || searchRaw !== ''

  const activeFilterCount = Object.values(values).filter(v => v !== '').length
    + (searchDebounced ? 1 : 0)

  const advancedFilterCount = useMemo(() => {
    let count = 0
    for (const f of config) {
      if (f.placement !== 'advanced') continue
      if (f.type === 'date-range') {
        if (values[`${f.key}_from`] || values[`${f.key}_to`]) count++
      } else if (f.type === 'number-range') {
        if (values[`${f.key}_min`] || values[`${f.key}_max`]) count++
      } else {
        if (values[f.key]) count++
      }
    }
    return count
  }, [config, values])

  return {
    values,
    searchRaw,
    setFilter,
    setRange,
    resetAll,
    resetFilter,
    params,
    hasActiveFilters,
    activeFilterCount,
    advancedFilterCount,
    searchOverridesDateRange,
  }
}
